import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthComponent';
import {
  AlertTriangleIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, ClockIcon, ExportIcon, InfoIcon,
  PlayIcon, SearchIcon, TransferIcon, XSmallIcon,
} from '@shopify/polaris-icons';
import { api } from './apiClient';
import { CategorySelect, MultiSelect } from './Selects';
import { Ic, StatusPill, EmDash } from '../ui';
import { AIPanel, AI_DEFAULTS, ComparePanel, V2Panel, V2_DEFAULTS, aiBody, useEngine, v2Body } from './Engines';

const DEFAULTS = {
  categoryIds: [], skuSearch: '', src: [], dest: [], days: 30,
  buffer: 5, bufferMode: 'units', thresh: 7, threshMode: 'days',
};
const PRIORITIES = [['all', 'All'], ['high', 'High'], ['med', 'Medium'], ['low', 'Low']];
const PRIORITY_NAME = { high: 'High', med: 'Medium', low: 'Low' };
const ENGINES = [['v1', 'V1 suggestions'], ['v2', 'V2 suggestions'], ['ai', 'AI suggestions'], ['cmp', 'Compare all 3']];
const TABS = [['transfers', 'Transfer suggestions', TransferIcon], ['velocity', 'Sales velocity', ClockIcon], ['stock', 'Stock by location', InfoIcon]];
const SCORE_PARTS = [['doi', 'Days-of-supply imbalance', 35], ['vel', 'Destination velocity', 25], ['perf', 'SKU performance', 25], ['units', 'Units surplus', 15]];
const JOBS = [['items', 'Items / SKUs'], ['velocity', 'Sales velocity'], ['item_shops_full', 'Stock (full)'], ['item_shops_incr', 'Stock (quick)']];
const TIER = { high: 0, med: 1, low: 2 };
const SORTS = {
  score: (a, b) => (TIER[a.priority] - TIER[b.priority]) || (b.score - a.score),
  product: (a, b) => a.product.localeCompare(b.product),
  category: (a, b) => a.category.localeCompare(b.category),
  transfer: (a, b) => a.src_name.localeCompare(b.src_name) || a.dest_name.localeCompare(b.dest_name),
  qty: (a, b) => a.move_qty - b.move_qty,
  srcleft: (a, b) => a.src_after - b.src_after,
  deststock: (a, b) => a.dest_stock - b.dest_stock,
  destdays: (a, b) => a.dest_days_after - b.dest_days_after,
  reason: (a, b) => a.reason.localeCompare(b.reason),
};
const COLUMNS = [['score', 'Priority / score'], ['product', 'Product'], ['category', 'Category'], ['transfer', 'Transfer'],
  ['qty', 'Qty'], ['srcleft', 'Src left'], ['deststock', 'Dest stock'], ['destdays', 'Dest days of supply'], ['reason', 'Reason']];

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const num = (n) => Number(n || 0).toLocaleString();
const money = (n) => Number(n || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const days = (d) => (d >= 999 ? '∞' : `${Math.round(d)}d`);
const key = (t) => `${t.item_id}:${t.src}:${t.dest}`;
const when = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
};
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

function Alert({ tone = '', children }) {
  const icon = tone === 'ok' ? CheckIcon : tone === 'info' ? InfoIcon : AlertTriangleIcon;
  return <div className={`uh-alert ${tone}`}><Ic source={icon} sm /><div>{children}</div></div>;
}

function InventoryRebalancing() {
  const { nameOf: personName } = useAuth();
  const [meta, setMeta] = useState(null);
  const [engines, setEngines] = useState(null);       // what the V2 and AI controls need: today's season, zones, defaults
  const [engine, setEngine] = useState('v1');         // which suggestions are on screen: v1 | v2 | ai | cmp
  const v2 = useEngine(api.v2Report, v2Body, V2_DEFAULTS);
  const ai = useEngine(api.aiReport, aiBody, AI_DEFAULTS);
  const [runningAll, setRunningAll] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [f, setF] = useState(DEFAULTS);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [used, setUsed] = useState(null);           // the filters the current report was run with
  const [live, setLive] = useState(null);           // { stock: {item: {shop: units}}, checked, requested }
  const [liveState, setLiveState] = useState('');   // '' | 'checking' | 'failed'
  const [tab, setTab] = useState('transfers');
  const [priFilter, setPriFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ col: 'score', dir: 'desc' });
  const [open, setOpen] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [dialog, setDialog] = useState(null);       // null | { groups, busy, error }
  const [created, setCreated] = useState(null);     // { created, errors } | { error }
  const [syncMsg, setSyncMsg] = useState('');
  const dialogRef = useRef(null);
  const runSeq = useRef(0);

  const loadStatus = useCallback(() => api.status().then(setStatus).catch((e) => setError(e.message)), []);
  useEffect(() => {
    api.meta().then(setMeta).catch((e) => setError(e.message));
    api.enginesMeta().then(setEngines).catch(() => {});
    loadStatus();
  }, [loadStatus]);
  // While a sync runs (started here, by cron, or by a colleague) keep the sync card current.
  useEffect(() => {
    if (!status?.sync_running) return undefined;
    const t = setInterval(loadStatus, 8000);
    return () => clearInterval(t);
  }, [status?.sync_running, loadStatus]);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (dialog && !d.open) d.showModal();
    if (!dialog && d.open) d.close();
  }, [dialog]);

  const shopOptions = useMemo(() => (meta?.shops || []).map((s) => ({ value: s.id, label: s.name })), [meta]);
  const allLocations = shopOptions.length ? `All ${shopOptions.length} locations` : 'All locations';
  const noData = status && !status.counts.stock_rows;

  async function runReport() {
    const seq = ++runSeq.current;
    setRunning(true); setError(''); setCreated(null); setLive(null); setLiveState('');
    try {
      const r = await api.report({
        category_ids: f.categoryIds, sku_search: f.skuSearch, src_shop_ids: f.src, dest_shop_ids: f.dest,
        days: f.days, buffer: f.buffer, buffer_mode: f.bufferMode, thresh: f.thresh, thresh_mode: f.threshMode,
      });
      if (seq !== runSeq.current) return;
      setReport(r); setUsed(f); setSelected(new Set()); setOpen(new Set()); setTab('transfers'); setPriFilter('all');
      loadStatus();
      if (r.transfers.length) verify(r, seq);
    } catch (e) {
      if (seq === runSeq.current) setError(e.message);
    } finally {
      if (seq === runSeq.current) setRunning(false);
    }
  }

  // Best-scoring items first: the server checks the first few hundred against live Lightspeed stock.
  async function verify(r, seq) {
    setLiveState('checking');
    try {
      const ids = [...new Set(r.transfers.map((t) => t.item_id))];
      const res = await api.validate(ids, Object.keys(r.shops));
      if (seq !== runSeq.current) return;
      const stock = {};
      res.stock.forEach((row) => { (stock[row.item_id] = stock[row.item_id] || {})[row.shop_id] = row.units; });
      setLive({ stock, checked: res.checked, requested: res.requested });
      setLiveState('');
    } catch {
      if (seq === runSeq.current) setLiveState('failed');
    }
  }

  const changed = (t) => {
    const l = live?.stock[t.item_id];
    return !!l && ((l[t.src] !== undefined && l[t.src] !== t.src_stock) || (l[t.dest] !== undefined && l[t.dest] !== t.dest_stock));
  };

  const rows = useMemo(() => {
    if (!report) return [];
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const base = report.transfers.filter((t) => (priFilter === 'all' || t.priority === priFilter)
      && terms.every((w) => `${t.sku} ${t.product}`.toLowerCase().includes(w)));
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => sign * SORTS[sort.col](a, b));
  }, [report, priFilter, search, sort]);

  const chosen = report ? report.transfers.filter((t) => selected.has(key(t))) : [];
  const allShown = rows.length > 0 && rows.every((t) => selected.has(key(t)));
  const flip = (setter, k) => setter((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    rows.forEach((t) => (allShown ? n.delete(key(t)) : n.add(key(t))));
    return n;
  });
  const sortBy = (col) => setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: col === 'score' ? 'desc' : 'asc' }));

  function exportCsv() {
    const head = ['Priority', 'Score', 'Product', 'SKU', 'Category', 'From', 'To', 'Move qty', 'Src remaining', 'Dest stock before',
      'Dest days before', 'Dest days after', 'Reason', 'Retail value', 'Cost'];
    const lines = report.transfers.map((t) => [PRIORITY_NAME[t.priority], t.score.toFixed(1), t.product, t.sku, t.category, t.src_name, t.dest_name,
      t.move_qty, t.src_after, t.dest_stock, t.dest_days_before >= 999 ? 'no sales' : Math.round(t.dest_days_before),
      t.dest_days_after >= 999 ? 'no sales' : Math.round(t.dest_days_after), t.reason, t.transfer_value, t.transfer_cost].map(csvCell).join(','));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([[head.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv' }));
    a.download = 'inventory-rebalancing.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const linesOf = (list) => list.map((t) => ({ src: t.src, dest: t.dest, item_id: t.item_id, qty: t.move_qty }));
  // Every tab creates transfers the same way: lines [{src, dest, item_id, qty}], names for the preview, and the report they came from.
  async function startCreate({ lines, labels, reportId, onDone }) {
    setDialog({ groups: null, busy: false, error: '', lines, labels, reportId, onDone });
    try {
      const r = await api.transfers(lines, true, reportId);
      setDialog((d) => d && { ...d, groups: r.groups });
    } catch (e) {
      setDialog((d) => d && { ...d, groups: [], error: e.message });
    }
  }
  const openCreate = () => startCreate({ lines: linesOf(chosen), reportId: report?.report_id, onDone: () => setSelected(new Set()),
    labels: Object.fromEntries(chosen.map((t) => [t.item_id, { product: t.product, sku: t.sku }])) });
  async function confirmCreate() {
    const d0 = dialog;
    setDialog((d) => ({ ...d, busy: true, error: '' }));
    try {
      const r = await api.transfers(d0.lines, false, d0.reportId);
      setCreated(r); d0.onDone?.(); setDialog(null);
    } catch (e) {
      setDialog((d) => ({ ...d, busy: false, error: e.message }));
    }
  }
  const shopNames = useMemo(() => Object.fromEntries((meta?.shops || []).map((x) => [x.id, x.name])), [meta]);
  const nameOf = (id) => shopNames[id] || report?.shops?.[id] || id;
  const lineName = (id) => dialog?.labels?.[id];
  async function runAll() {
    setRunningAll(true);
    await Promise.all([runReport(), v2.run(), ai.run()]);
    setRunningAll(false);
  }

  async function startSync(kind) {
    setSyncMsg('');
    try {
      const r = await api.sync(kind);
      setSyncMsg(r.started ? (kind === 'full' ? 'Full sync started. It takes about 6 hours.' : 'Quick refresh started.') : r.reason);
      setTimeout(loadStatus, 2500);
    } catch (e) {
      setSyncMsg(e.message);
    }
  }

  const counts = status?.counts;
  const job = (k) => status?.jobs?.[k];
  const syncing = !!status?.sync_running;
  const products = report?.products || [];
  const shopIds = report ? Object.keys(report.shops) : [];

  const stat = (label, value, filter) => {
    const body = (<><span className="v">{report ? value : <EmDash />}</span><span className="k">{label}</span></>);
    return filter ? (
      <button type="button" className="uh-stat" aria-pressed={priFilter === filter}
        onClick={() => { setPriFilter(priFilter === filter ? 'all' : filter); setTab('transfers'); }}>{body}</button>
    ) : <div className="uh-stat">{body}</div>;
  };

  return (
    <div className="uh uh-page">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Inventory rebalancing</h1>
          <div className="uh-sub">Ranks transfers between Soccer Post’s Lightspeed locations from cached stock and sales velocity, then creates the transfer documents in Lightspeed.</div>
        </div>
        <div className="uh-statcard uh-ir-sync" aria-label="Data sync">
          <div className="hd">
            <div className="ttl"><b>Data sync</b>
              {syncing ? <StatusPill tone="hold">Syncing…</StatusPill> : <StatusPill tone={noData ? 'hold' : 'ful'}>{noData ? 'Not loaded' : 'Idle'}</StatusPill>}
            </div>
            <span className="uh-help">
              {counts ? `${counts.shops_synced}/${counts.shops} locations · ${num(counts.stock_rows)} stock records${counts.ignored ? ` · ${num(counts.ignored)} items hidden` : ''}` : 'Loading…'}
            </span>
          </div>
          <div className="jobs">
            {JOBS.map(([k, label]) => {
              const j = job(k);
              const bad = j && (j.status === 'error' || j.status === 'interrupted');
              return (
                <div key={k} className="job" title={j?.error || j?.note || ''}>
                  <span className="jl">{label}</span>
                  <span className={`js${bad ? ' bad' : ''}`}>
                    {!j ? 'Never synced' : j.status === 'running' ? 'Running…' : bad ? (j.status === 'error' ? 'Error' : 'Interrupted') : `${num(j.records)} records`}
                  </span>
                  <span className="jt">{j ? when(j.finished_at || j.started_at) : ''}</span>
                </div>
              );
            })}
          </div>
          <div className="act">
            <ul className="sched"><li><b>Overnight</b> Full sync (about 6 h)</li><li><b>Business day</b> Quick refresh every 4 h</li></ul>
            <div className="btns">
              <button type="button" className="uh-btn" disabled={syncing} onClick={() => startSync('quick')}><Ic source={ClockIcon} sm />Quick refresh</button>
              <button type="button" className="uh-btn" disabled={syncing} onClick={() => startSync('full')}>Full sync</button>
            </div>
          </div>
        </div>
      </div>

      <div className="uh-ir-engines" role="tablist" aria-label="Suggestions">
        {ENGINES.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={engine === k} onClick={() => setEngine(k)}>
            {label}
            {k === 'v1' && report && <span className="uh-tcount">{report.total}</span>}
            {k === 'v2' && v2.result && <span className="uh-tcount">{v2.result.summary.transfers}</span>}
            {k === 'ai' && ai.result && <span className="uh-tcount">{ai.result.summary.transfers}</span>}
          </button>
        ))}
      </div>
      {created && (
        <div className="uh-ir-banner">
          {created.error ? <Alert tone="crit">{created.error}</Alert> : (
            <Alert tone={created.errors?.length ? '' : 'ok'}>
              <b>{plural(created.created.length, 'transfer document')} created in Lightspeed.</b>{' '}
              {created.created.length > 0 && (
                <>Transfer {created.created.map((c) => `#${c.transfer_id} (${nameOf(c.src)} → ${nameOf(c.dest)})`).join(', ')}. Open {created.created.length === 1 ? 'it' : 'them'} in Lightspeed to send.</>
              )}
              {created.errors?.length > 0 && ` ${plural(created.errors.length, 'pair')} failed: ${created.errors.map((e) => `${nameOf(e.src)} → ${nameOf(e.dest)} (${e.error})`).join('; ')}.`}
            </Alert>
          )}
        </div>
      )}
      {error && <Alert tone="crit">{error}</Alert>}
      {syncMsg && <Alert tone="info">{syncMsg}</Alert>}
      {noData && (
        <Alert>The Lightspeed data hasn’t been loaded yet. Start a full sync above (about 6 hours); reports work once stock has loaded.</Alert>
      )}

      {engine === 'v2' && <V2Panel meta={meta} engines={engines} eng={v2} onCreate={startCreate} />}
      {engine === 'ai' && <AIPanel meta={meta} eng={ai} onCreate={startCreate} />}
      {engine === 'cmp' && <ComparePanel v1={report} v2={v2.result} ai={ai.result} runAll={runAll} running={runningAll} />}

      {engine === 'v1' && (<>
      <div className="uh-start">
        <div className="uh-col">
          <div className="uh-card uh-card-b uh-form-card">
            <div className="uh-note">
              <Ic source={InfoIcon} sm />
              <div>Reports read the synced Lightspeed data, so they run in seconds. Suggested lines are then checked against live Lightspeed stock, and any that moved are flagged.</div>
            </div>
            <div className="uh-grid2">
              <div className="uh-fld"><label htmlFor="ir-cat">Item categories <span className="optl">(blank = all)</span></label>
                <CategorySelect id="ir-cat" categories={meta?.categories || []} value={f.categoryIds} onChange={(v) => set({ categoryIds: v })} /></div>
              <div className="uh-fld"><label htmlFor="ir-days">Analysis period</label>
                <select id="ir-days" className="uh-inp" value={f.days} onChange={(e) => set({ days: Number(e.target.value) })}>
                  <option value={30}>Last 30 days</option><option value={60}>Last 60 days</option><option value={90}>Last 90 days</option>
                </select></div>
              <div className="uh-fld"><label htmlFor="ir-sku">SKU contains <span className="optl">(blank = all)</span></label>
                <input id="ir-sku" className="uh-inp" placeholder="e.g. 030989-04" value={f.skuSearch} onChange={(e) => set({ skuSearch: e.target.value })} /></div>
              <div className="uh-fld"><label htmlFor="ir-src">Source locations <span className="optl">(transfer FROM, blank = all)</span></label>
                <MultiSelect id="ir-src" noun="locations" options={shopOptions} value={f.src} onChange={(v) => set({ src: v })} allLabel={allLocations} /></div>
              <div className="uh-fld"><label htmlFor="ir-dest">Destination locations <span className="optl">(receive stock, blank = all)</span></label>
                <MultiSelect id="ir-dest" noun="locations" options={shopOptions} value={f.dest} onChange={(v) => set({ dest: v })} allLabel={allLocations} /></div>
              <div className="uh-fld"><label htmlFor="ir-buf">Min supply to keep at source</label>
                <div className="uh-ir-cmp">
                  <input id="ir-buf" className="uh-inp uh-num" type="number" min={0} max={999} value={f.buffer} onChange={(e) => set({ buffer: Math.max(0, parseInt(e.target.value, 10) || 0) })} />
                  <select className="uh-inp" aria-label="Unit for minimum supply" value={f.bufferMode} onChange={(e) => set({ bufferMode: e.target.value })}>
                    <option value="units">Units</option><option value="days">Days of supply</option></select></div></div>
              <div className="uh-fld"><label htmlFor="ir-thr">Low stock threshold (destination)</label>
                <div className="uh-ir-cmp">
                  <input id="ir-thr" className="uh-inp uh-num" type="number" min={1} max={999} value={f.thresh} onChange={(e) => set({ thresh: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
                  <select className="uh-inp" aria-label="Unit for low stock threshold" value={f.threshMode} onChange={(e) => set({ threshMode: e.target.value })}>
                    <option value="days">Days of supply</option><option value="units">Units</option></select></div></div>
            </div>
            <div className="uh-actions">
              <button type="button" className="uh-btn" onClick={() => setF(DEFAULTS)}>Reset filters</button>
              <button type="button" className={`uh-btn primary wide${running ? ' busy' : ''}`} disabled={running || !meta || noData} onClick={runReport}>
                <Ic source={PlayIcon} sm />{running ? 'Building report…' : 'Run report'}
              </button>
              <span className="uh-help">
                {f.src.length || f.dest.length ? `${new Set([...f.src, ...f.dest]).size} location(s) selected` : allLocations}
                {f.categoryIds.length ? ` · ${plural(f.categoryIds.length, 'category', 'categories')}` : ' · All categories'}
              </span>
            </div>
          </div>

          <div className="uh-statcard uh-ir-results" aria-label="Report results">
            <div className="state">
              <span className="ic"><Ic source={CheckIcon} /></span>
              <div><b>Lightspeed data</b><span className="line">{counts?.latest_refresh ? `Refreshed ${when(counts.latest_refresh)}` : 'Not refreshed yet'}</span></div>
            </div>
            <div className="stats">
              {stat('Transfers suggested', report?.total, '')}
              {stat('High', report?.high, 'high')}
              {stat('Medium', report?.med, 'med')}
              {stat('Low', report?.low, 'low')}
              {stat('SKUs', report?.skus, '')}
              {stat('Receiving locations', report?.locs, '')}
              {stat('Retail value', report ? money(report.value) : '', '')}
            </div>
          </div>
        </div>

        <div className="uh-card uh-card-b">
          <h2 className="uh-h2 lg">How suggestions are scored</h2>
          <div className="uh-ir-weights">
            {SCORE_PARTS.map(([k, label, w]) => (
              <div key={k} className="w"><span>{label}</span><b>{w}%</b><i style={{ '--w': `${w}%` }} /></div>
            ))}
          </div>
          <div className="uh-ir-tiers">
            <span><i className="can" />High ≥ 70</span><span><i className="hold" />Medium 40–69</span><span><i className="low" />Low 15–39</span><span>Under 15: hidden</span>
          </div>
          <p className="uh-help" style={{ marginTop: 10 }}>
            Items with no sale anywhere in 60+ days are skipped. Quantities equalize days of supply between the two locations, never below the threshold.
          </p>
        </div>
      </div>

      <div className="uh-card">
        <div className="uh-tabs" role="tablist" aria-label="Report">
          {TABS.map(([k, label, icon]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
              <Ic source={icon} sm />{label}
              {k === 'transfers' && report && <span className="uh-tcount">{report.total}</span>}
            </button>
          ))}
        </div>

        {!report && (
          <div className="uh-empty">{running ? 'Building the report…' : 'Choose your filters and run a report to see suggested transfers.'}</div>
        )}

        {report && (
          <div className="uh-ir-chips">
            <span className="chip">Last {used.days}d</span>
            <span className="chip">{used.categoryIds.length ? plural(used.categoryIds.length, 'category', 'categories') : 'All categories'}</span>
            <span className="chip">{used.src.length || used.dest.length ? `${plural(shopIds.length, 'location')}` : 'All locations'}</span>
            <span className="chip">Keep ≥{used.buffer}{used.bufferMode === 'days' ? 'd supply' : ' units'} at source · Flag &lt;{used.thresh}{used.threshMode === 'days' ? 'd supply' : ' units'} at destination{used.skuSearch.trim() ? ` · SKU contains “${used.skuSearch.trim()}”` : ''}</span>
            {liveState === 'checking' && <span className="chip">Verifying against live Lightspeed…</span>}
            {live && <span className="chip ok"><Ic source={CheckIcon} sm />Live verified{live.checked < live.requested ? ` (top ${live.checked} of ${live.requested} items)` : ''}</span>}
            {liveState === 'failed' && <span className="chip bad">Live verification unavailable</span>}
          </div>
        )}

        {report && tab === 'transfers' && (
          <>
            <div className="uh-ir-tbar">
              <div className="uh-search" style={{ flex: '1 1 300px', maxWidth: 420 }}>
                <Ic source={SearchIcon} sm />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="SKU or product name, all terms must match" aria-label="Search transfers" />
              </div>
              {PRIORITIES.map(([k, label]) => (
                <button key={k} type="button" className="uh-ir-pill" aria-pressed={priFilter === k} onClick={() => setPriFilter(k)}>{label}</button>
              ))}
              <span className="uh-sp" />
              <button type="button" className="uh-btn" onClick={exportCsv}><Ic source={ExportIcon} sm />Export CSV</button>
            </div>

            <div className="uh-tscroll">
              <table className="uh-tbl uh-ir-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}><input type="checkbox" checked={allShown} onChange={toggleAll} aria-label="Select all shown" /></th>
                    {COLUMNS.map(([col, label]) => (
                      <th key={col} aria-sort={sort.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                        <button type="button" className="uh-ir-th" onClick={() => sortBy(col)}>{label}<span aria-hidden="true">{sort.col === col ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</span></button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={10} className="uh-empty">No transfers for this filter</td></tr>}
                  {rows.map((t) => {
                    const k = key(t);
                    const isOpen = open.has(k);
                    const stale = changed(t);
                    return (
                      <Fragment key={k}>
                        <tr className={`click${stale ? ' stale' : ''}`} onClick={() => flip(setOpen, k)}>
                          <td onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={selected.has(k)} onChange={() => flip(setSelected, k)} aria-label={`Select ${t.product}, ${t.src_name} to ${t.dest_name}`} />
                          </td>
                          <td>
                            <button type="button" className="uh-ir-pri" aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} score breakdown`}
                              onClick={(e) => { e.stopPropagation(); flip(setOpen, k); }}>
                              <Ic source={isOpen ? ChevronDownIcon : ChevronRightIcon} sm /><i className={t.priority} />{PRIORITY_NAME[t.priority]}
                            </button>
                            <div className="uh-help uh-num" style={{ marginLeft: 22 }}>{t.score.toFixed(1)}</div>
                          </td>
                          <td><div className="nm">{t.product}</div><div className="sm uh-mono">{t.sku}</div></td>
                          <td><span className="uh-ir-cat">{t.category}</span></td>
                          <td><span className="uh-ir-route"><span className="loc">{t.src_name}</span>→<span className="loc">{t.dest_name}</span></span></td>
                          <td><span className="uh-ir-qty uh-num">{t.move_qty}</span></td>
                          <td className="r uh-num">{t.src_after}</td>
                          <td className="uh-num"><span className="uh-muted">{t.dest_stock}</span> → <b>{t.dest_stock + t.move_qty}</b></td>
                          <td className="uh-num"><span className="uh-muted">{days(t.dest_days_before)}</span> → <b className="uh-ir-good">{days(t.dest_days_after)}</b></td>
                          <td><span className={`uh-ir-why ${t.priority}`}>{t.reason}</span>{stale && <span className="uh-ir-chg">stock changed</span>}</td>
                        </tr>
                        {isOpen && (
                          <tr className="uh-x">
                            <td colSpan={10}>
                              <div className="uh-ir-xg">
                                <div>
                                  <div className="uh-ir-xt">Score breakdown</div>
                                  {SCORE_PARTS.map(([part, label, w]) => (
                                    <div key={part} className="uh-ir-sr">
                                      <span>{label}</span>
                                      <span className="tr"><i style={{ width: `${Math.min(Math.round(t.scores[part]), 100)}%` }} /></span>
                                      <span className="vv uh-num">{(t.scores[part] * w / 100).toFixed(1)}<small>/{w}</small></span>
                                    </div>
                                  ))}
                                  <div className="uh-help"><b>Total {t.score.toFixed(1)} / 100</b></div>
                                </div>
                                <div>
                                  <div className="uh-ir-xt">Transfer context</div>
                                  <div className="uh-kv">
                                    <span>Source supply</span><b>{days(t.src_doi)} → {days(t.src_vel > 0 ? t.src_after / t.src_vel : t.src_after > 0 ? 999 : 0)} after</b>
                                    <span>Destination supply</span><b>{days(t.dest_days_before)} → {days(t.dest_days_after)} after</b>
                                    <span>Source velocity</span><b>{t.src_vel.toFixed(2)}/day</b>
                                    <span>Destination velocity</span><b>{t.dest_vel.toFixed(2)}/day{t.net_avg > 0 ? ` (network avg ${t.net_avg.toFixed(2)})` : ''}</b>
                                    {t.transfer_value > 0 && (<><span>Retail value</span><b>${t.transfer_value.toFixed(2)}</b></>)}
                                    {t.transfer_cost > 0 && (<><span>Cost</span><b>${t.transfer_cost.toFixed(2)}</b></>)}
                                    {stale && (<><span>Live stock</span><b>{nameOf(t.src)} {live.stock[t.item_id]?.[t.src] ?? '?'} · {nameOf(t.dest)} {live.stock[t.item_id]?.[t.dest] ?? '?'}</b></>)}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="uh-tfoot"><span>{rows.length} of {report.total} suggested transfers</span><span className="uh-sp" /><span>Click a row for its score breakdown.</span></div>
            {chosen.length > 0 && (
              <div className="uh-ir-abar">
                <b>{plural(chosen.length, 'transfer')} selected</b>
                <span className="uh-ir-abar-s">{plural(new Set(chosen.map((t) => `${t.src}>${t.dest}`)).size, 'transfer document')} in Lightspeed</span>
                <span className="uh-sp" />
                <button type="button" className="uh-btn ghost" onClick={() => setSelected(new Set())}>Clear</button>
                <button type="button" className="uh-btn primary" onClick={openCreate}><Ic source={TransferIcon} sm />Create in Lightspeed…</button>
              </div>
            )}
          </>
        )}

        {report && tab !== 'transfers' && (
          <DataTab kind={tab} products={products} shops={report.shops} thresh={used.thresh} total={report.products_total} />
        )}
      </div>

      </>)}

      <dialog className="uh-dlg uh-ir-dlg" ref={dialogRef} aria-labelledby="ir-dlg-h" onCancel={() => !dialog?.busy && setDialog(null)}>
        <div className="uh-dlg-h">
          <h2 id="ir-dlg-h">Create transfers in Lightspeed</h2>
          <button type="button" className="uh-ibtn" aria-label="Close" disabled={dialog?.busy} onClick={() => setDialog(null)}><Ic source={XSmallIcon} /></button>
        </div>
        <div className="uh-dlg-b">
          {dialog?.error && <Alert tone="crit">{dialog.error}</Alert>}
          {dialog && !dialog.groups && !dialog.error && <span className="uh-muted">Preparing the transfer documents…</span>}
          {dialog?.groups?.length > 0 && (
            <>
              <Alert>
                This creates {plural(dialog.groups.length, 'real transfer document')} in Lightspeed ({plural(dialog.lines.length, 'line')}).
                {status && !status.can_create_transfers && ' It is turned off on development servers.'}
              </Alert>
              {dialog.groups.map((g) => (
                <div key={`${g.src}>${g.dest}`} className="uh-ir-grp">
                  <div className="gh">{nameOf(g.src)} → {nameOf(g.dest)}<span className="uh-sp" /><small>{plural(g.lines.length, 'line')}</small></div>
                  {g.lines.map((l) => (
                    <div key={l.item_id} className="gl">
                      <span>{lineName(l.item_id)?.product} <span className="uh-mono uh-muted">{lineName(l.item_id)?.sku}</span></span>
                      <span className="q uh-num">{l.qty}</span>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
        <div className="uh-dlg-f">
          <button type="button" className="uh-btn" disabled={dialog?.busy} onClick={() => setDialog(null)}>Cancel</button>
          <button type="button" className="uh-btn danger" disabled={dialog?.busy || !dialog?.groups?.length || (status && !status.can_create_transfers)} onClick={confirmCreate}>
            <Ic source={TransferIcon} sm />{dialog?.busy ? 'Creating…' : `Create ${plural(dialog?.groups?.length || 0, 'transfer')} in Lightspeed`}
          </button>
        </div>
      </dialog>

      {status?.usage?.length > 0 && (
        <div className="uh-card">
          <div className="uh-card-h"><h2 className="uh-h2">Usage</h2><span className="uh-help">Reports run, by person</span></div>
          <div className="uh-tscroll">
            <table className="uh-tbl">
              <thead><tr><th>User</th><th className="r">Reports</th><th>Last run</th><th className="r">Avg items</th><th className="r">Avg locations</th></tr></thead>
              <tbody>
                {status.usage.map((u) => (
                  <tr key={u.username}><td className="nm">{personName(u.username)}</td><td className="r uh-num">{num(u.reports)}</td><td>{when(u.last_ran_at)}</td>
                    <td className="r uh-num">{num(u.avg_items)}</td><td className="r uh-num">{num(u.avg_shops)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Sales velocity and Stock by location: the busiest items, one column (or pair) per location.
function DataTab({ kind, products, shops, thresh, total }) {
  const [q, setQ] = useState('');
  const ids = Object.keys(shops);
  const term = q.trim().toLowerCase();
  const list = products.filter((p) => (kind === 'velocity' ? ids.some((s) => p.velocity[s] > 0) : ids.some((s) => p.stock[s] > 0))
    && (!term || `${p.name} ${p.sku}`.toLowerCase().includes(term)));
  const maxV = Math.max(0.01, ...list.flatMap((p) => ids.map((s) => p.velocity[s] || 0)));
  return (
    <>
      <div className="uh-ir-tbar">
        <div className="uh-search" style={{ flex: '1 1 300px', maxWidth: 420 }}>
          <Ic source={SearchIcon} sm />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by SKU or product name" aria-label="Filter items" />
        </div>
        <span className="uh-help">{list.length} of {total} items{total > products.length ? ` (busiest ${products.length} shown)` : ''}</span>
      </div>
      <div className="uh-tscroll">
        <table className="uh-tbl uh-ir-tbl">
          <thead>
            <tr>
              <th>Product</th><th>Category</th>
              {ids.map((s) => (kind === 'velocity' ? <th key={s} className="c">{shops[s]}</th> : <Fragment key={s}><th className="c">{shops[s]} stock</th><th className="c">days</th></Fragment>))}
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={ids.length * 2 + 2} className="uh-empty">Nothing matches</td></tr>}
            {list.map((p) => (
              <tr key={p.id}>
                <td><div className="nm">{p.name}</div><div className="sm uh-mono">{p.sku}</div></td>
                <td><span className="uh-ir-cat">{p.category}</span></td>
                {ids.map((s) => {
                  const v = p.velocity[s] || 0;
                  const qty = p.stock[s] || 0;
                  if (kind === 'velocity') {
                    return <td key={s} className="uh-num c"><div className="uh-ir-vbar" style={{ width: Math.round((v / maxV) * 46) }} />{v.toFixed(2)}</td>;
                  }
                  const d = v > 0 ? Math.round(qty / v) : qty > 0 ? 999 : 0;
                  const tone = qty === 0 ? 'out' : d < thresh ? 'low' : 'ok';
                  return (
                    <Fragment key={s}>
                      <td className="uh-num c"><b>{qty}</b></td>
                      <td className="c"><span className={`uh-ir-cell ${tone}`}>{qty === 0 ? 'Out' : d >= 999 ? 'No sales' : `${d}d`}</span></td>
                    </Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default InventoryRebalancing;
